import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, FlaskConical } from 'lucide-react';
import Modal from '../ui/Modal';
import { useOrg } from '../../hooks/useOrg';
import { useNotifications } from '../NotificationSystem';
import { completeATPTask } from '../../services/taskService';
import './CompleteATPModal.css';

/**
 * Complete ATP check task — room/surface readonly from task.atp_context (scheduler).
 * User selects interpretation (conform/neconform) + optional description.
 */
function CompleteATPModal({ isOpen, onClose, task, userId, onCompleted }) {
  const { t } = useTranslation(['ipc', 'tasks', 'common']);
  const { activeOrgId } = useOrg();
  const { notify } = useNotifications();

  const [testResult, setTestResult] = useState(null);
  const [observations, setObservations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTestResult(null);
      setObservations('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!task) return null;

  const ctx = task.atp_context || {};
  const roomLine = [ctx.room_code, ctx.room_name].filter(Boolean).join(' — ') || ctx.room_name || '—';

  const isValid = testResult === 'negative' || testResult === 'positive';

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!isValid || isSubmitting) return;

    if (task.organization_id && activeOrgId && task.organization_id !== activeOrgId) {
      notify.error(t('ipc:atp.organizationMismatch'));
      return;
    }

    const resultData = {
      interpretation: testResult === 'negative' ? 'conform' : 'neconform',
      value: null,
      description: observations.trim() || ''
    };

    setIsSubmitting(true);
    try {
      await completeATPTask(task.id, resultData, userId);
      notify.success(t('tasks:atpTaskCompleted'));
      onClose();
      if (typeof onCompleted === 'function') onCompleted();
    } catch (error) {
      console.error('[CompleteATPModal]', error);
      notify.error(t('tasks:atpTaskError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('ipc:atp.modalTitle')}
      size="medium"
    >
      <form className="atp-modal-form" onSubmit={handleSubmit}>
        <div className="form-field readonly-field">
          <label>{t('ipc:atp.preselectedRoom')}</label>
          <div className="readonly-value">
            <MapPin size={16} aria-hidden />
            <span>{roomLine}</span>
          </div>
        </div>

        <div className="form-field readonly-field">
          <label>{t('ipc:atp.preselectedSurface')}</label>
          <div className="readonly-value">
            <FlaskConical size={16} aria-hidden />
            <span>{ctx.surface_type_name || '—'}</span>
          </div>
        </div>

        <div className="form-field">
          <span className="field-label">{t('ipc:atp.testResult')} *</span>
          <div className="radio-group" role="radiogroup" aria-label={t('ipc:atp.testResult')}>
            <label className="radio-option">
              <input
                type="radio"
                name="atp-result"
                value="negative"
                checked={testResult === 'negative'}
                onChange={() => setTestResult('negative')}
                disabled={isSubmitting}
              />
              <span className="badge badge-success">
                {t('ipc:atp.negative')}
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="atp-result"
                value="positive"
                checked={testResult === 'positive'}
                onChange={() => setTestResult('positive')}
                disabled={isSubmitting}
              />
              <span className="badge badge-danger">
                {t('ipc:atp.positive')}
              </span>
            </label>
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="atp-observations">{t('ipc:atp.observations')}</label>
          <textarea
            id="atp-observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value.slice(0, 500))}
            placeholder={t('ipc:atp.observationsPlaceholder')}
            disabled={isSubmitting}
            rows={3}
            maxLength={500}
          />
          <small className="char-count">
            {observations.length}/500
          </small>
        </div>

        <div className="submit-section">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            {t('common:cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? t('ipc:atpScheduler.saving') : t('ipc:atp.submitCompletion')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CompleteATPModal;
